<script setup>
/**
 * @fileoverview ConsoleSessionsHost - global overlay that renders all open
 * console sessions. Mounted once in AppLayout.
 *
 * Each session lives in its own absolutely-positioned container. Only the
 * `activeVmId` session is `display: block`; all others are `display: none`
 * but their controllers (and WebSocket tunnels) keep running so users can
 * switch between them instantly.
 *
 * The host itself is hidden when no session is active so that minimized
 * sessions still run in the background but the page below remains visible
 * (only the dock is shown).
 */
import { computed } from 'vue'
import { useConsoleSessionsStore } from '@/stores/consoleSessions'
import ConsoleSessionView from './ConsoleSessionView.vue'

const sessionsStore = useConsoleSessionsStore()

const sessions = computed(() => sessionsStore.sessionList)
const activeVmId = computed(() => sessionsStore.activeVmId)
const isHostVisible = computed(() => sessionsStore.isHostVisible)
</script>

<template>
  <div
    v-if="sessions.length > 0"
    class="console-sessions-host fixed inset-0 z-[45] bg-gray-50 dark:bg-slate-900"
    :class="isHostVisible ? 'block' : 'hidden'"
    aria-label="Active console sessions"
  >
    <div
      v-for="session in sessions"
      :key="session.sessionKey"
      class="session-slot"
      :style="{ display: session.sessionKey === activeVmId ? 'block' : 'none' }"
      :aria-hidden="session.sessionKey !== activeVmId"
    >
      <ConsoleSessionView :vm-id="session.vmId" :session-key="session.sessionKey" />
    </div>
  </div>
</template>

<style scoped>
.session-slot {
  position: absolute;
  inset: 0;
}
</style>
